import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Shell, Wordmark } from "./components/Shell";
import { Button, ErrorNote, Spinner, ToastHost } from "./components/ui";
import { api, type Me } from "./lib/api";
import { ConnectionsProvider } from "./lib/connections";
import { boot, completeSignIn, NotFound, pendingFreshSignIn, signedIn, signIn, signInReady, type SiteInfo } from "./lib/auth";
import ActorMapping from "./pages/ActorMapping";
import AppPlaceholder from "./pages/AppPlaceholder";
import JaAutomation from "./pages/ja/JaAutomation";
import JaHome from "./pages/ja/JaHome";
import JaDesigner from "./pages/ja/designer/JaDesigner";
import JaRun from "./pages/ja/JaRun";
import JaRuns from "./pages/ja/JaRuns";
import JaRunStatus from "./pages/ja/JaRunStatus";
import Attributes from "./pages/Attributes";
import SignInSettings from "./pages/SignInSettings";
import Connections from "./pages/Connections";
import Launcher from "./pages/Launcher";
import Tenants from "./pages/Tenants";
import UsersPage from "./pages/Users";
import RadarHome from "./pages/radar/RadarHome";
import RadarJob from "./pages/radar/RadarJob";
import RadarSettings from "./pages/RadarSettings";
import { SqlLibraryEntry, SqlLibraryList } from "./pages/radar/SqlLibrary";

type State = { step: "loading" } | { step: "notfound" } | { step: "error"; message: string } | { step: "signin"; site: SiteInfo; error?: string } | { step: "ready"; me: Me };

export default function App() {
  const [state, setState] = useState<State>({ step: "loading" });
  useEffect(() => {
    (async () => {
      let site: SiteInfo;
      try {
        site = (await boot()).site;
      } catch (e) {
        return setState(e instanceof NotFound ? { step: "notfound" } : { step: "error", message: (e as Error).message });
      }
      // Back from signIn()'s /logout hop: carry straight on to the sign-in page.
      const pending = pendingFreshSignIn();
      if (pending && !signedIn()) return void signIn(pending.back, { portal: pending.portal });
      const error = await completeSignIn();
      if (error || !signedIn()) return setState({ step: "signin", site, error: error ?? undefined });
      try {
        setState({ step: "ready", me: await api<Me>("/me") });
      } catch (e) {
        setState({ step: "signin", site, error: (e as Error).message });
      }
    })();
  }, []);

  if (state.step === "loading") return <Spinner />;
  if (state.step === "notfound") return <Centered title="Portal not found">There's no Intellias App Portal at this address. Check the link your administrator sent you.</Centered>;
  if (state.step === "error") return <Centered title="Something went wrong">{state.message}</Centered>;
  if (state.step === "signin") return <SignIn site={state.site} error={state.error} />;

  const me = state.me;
  return (
    <BrowserRouter>
      <ConnectionsProvider enabled={me.kind === "tenant"}>
      <Shell me={me}>
        <Routes>
          {me.kind === "platform" ? (
            <Route path="/" element={<Tenants />} />
          ) : (
            <>
              <Route path="/" element={<Launcher me={me} />} />
              {me.tenantAdmin && <Route path="/admin/users" element={<UsersPage me={me} />} />}
              {me.tenantAdmin && <Route path="/admin/connections" element={<Connections />} />}
              {me.tenantAdmin && <Route path="/admin/connections/:connId/actors" element={<ActorMapping />} />}
              {me.tenantAdmin && <Route path="/admin/attributes" element={<Attributes />} />}
              {me.tenantAdmin && <Route path="/admin/sign-in" element={<SignInSettings me={me} />} />}
              {me.tenantAdmin && me.tenant?.apps.includes("radar") && <Route path="/admin/radar" element={<RadarSettings />} />}
              {me.apps?.some((a) => a.id === "radar") && <Route path="/apps/radar" element={<RadarHome me={me} />} />}
              {me.apps?.some((a) => a.id === "radar") && <Route path="/apps/radar/jobs/:jobId" element={<RadarJob me={me} />} />}
              {me.apps?.find((a) => a.id === "radar")?.roles.includes("sql") && <Route path="/apps/radar/sql" element={<SqlLibraryList />} />}
              {me.apps?.find((a) => a.id === "radar")?.roles.includes("sql") && <Route path="/apps/radar/sql/:id" element={<SqlLibraryEntry />} />}
              {me.apps?.some((a) => a.id === "ja") && <Route path="/apps/ja" element={<JaHome me={me} />} />}
              {me.apps?.some((a) => a.id === "ja") && <Route path="/apps/ja/automations/:id" element={<JaAutomation me={me} />} />}
              {me.apps?.some((a) => a.id === "ja") && <Route path="/apps/ja/automations/:id/run" element={<JaRun />} />}
              {me.apps?.find((a) => a.id === "ja")?.roles.includes("designer") && <Route path="/apps/ja/automations/:id/design" element={<JaDesigner />} />}
              {me.apps?.some((a) => a.id === "ja") && <Route path="/apps/ja/runs" element={<JaRuns />} />}
              {me.apps?.some((a) => a.id === "ja") && <Route path="/apps/ja/runs/:runId" element={<JaRunStatus me={me} />} />}
              <Route path="/apps/:appId/*" element={<AppPlaceholder me={me} />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
      </ConnectionsProvider>
      <ToastHost />
    </BrowserRouter>
  );
}

function SignIn({ site, error }: { site: SiteInfo; error?: string }) {
  const title = site.kind === "platform" ? "Platform console" : site.tenant.name;
  return (
    <Centered title={title}>
      <div className="space-y-4">
        {error && <ErrorNote>{error}</ErrorNote>}
        {signInReady() && (
          <Button variant="primary" className="w-full h-10" onClick={() => void signIn()}>
            {site.kind === "tenant" && site.identityProvider ? "Sign in with your organization" : "Sign in"}
          </Button>
        )}
        {site.kind === "tenant" && site.identityProvider && site.portalSignIn && (
          <Button className="w-full h-10" onClick={() => void signIn(undefined, { portal: true })}>
            Sign in with a portal account
          </Button>
        )}
        {site.kind === "tenant" && site.setupPending && <p className="text-xs text-muted">Single sign-on for {title} is still being set up. Until then, sign in with the portal account from your invitation email.</p>}
      </div>
    </Centered>
  );
}

function Centered({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-full flex flex-col">
      <div className="brand-bar h-1" aria-hidden />
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-sm bg-surface border border-line rounded-2xl p-8 shadow-sm">
          <Wordmark className="h-7" />
          <h1 className="text-xl font-bold text-ink mt-6 mb-1">{title}</h1>
          <p className="text-xs text-muted mb-5">Intellias App Portal</p>
          <div className="text-sm text-ink-2">{children}</div>
        </div>
      </div>
      <div className="text-center text-xs text-muted py-4">Powered by Intellias</div>
    </div>
  );
}
